import React, { FC, useCallback, useEffect, useRef } from 'react';
import { INITIAL_PAGINATION } from 'core/constants';
import { SetPagination } from 'core/types';
import Loader from '../Loader';

type ObserverParentProps = {
	paginationSkip: number;
	setPagination: SetPagination;
	isLoading: boolean;
};

const Observer: FC<ObserverParentProps> = ({ paginationSkip, setPagination, isLoading }: ObserverParentProps) => {
	const observerRef = useRef<HTMLDivElement>(null);
	const isLoadingRef = useRef(isLoading);
	const lastRequestedSkipRef = useRef<number | null>(null);

	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	useEffect(() => {
		if (lastRequestedSkipRef.current !== null && lastRequestedSkipRef.current !== paginationSkip) {
			lastRequestedSkipRef.current = null;
		}
	}, [paginationSkip]);

	const addMoreOpportunities = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			if (isLoadingRef.current) return;
			entries.forEach((entry: IntersectionObserverEntry) => {
				if (entry.isIntersecting && entry.intersectionRatio > 0) {
					if (lastRequestedSkipRef.current === paginationSkip) return;
					lastRequestedSkipRef.current = paginationSkip;
					setPagination && setPagination({ skip: paginationSkip, take: INITIAL_PAGINATION.take });
				}
			});
		},
		[paginationSkip, setPagination]
	);

	useEffect(() => {
		const observerOptions: IntersectionObserverInit = {
			threshold: 0.1,
		};
		const observer = new IntersectionObserver(addMoreOpportunities, observerOptions);
		const target = observerRef.current;
		if (target && paginationSkip > 0) {
			observer.observe(target);
		}

		return () => {
			observer.disconnect();
		};
	}, [addMoreOpportunities, paginationSkip]);

	return (
		<div>
			<div ref={observerRef} />
			{isLoading && <Loader />}
		</div>
	);
};

export default Observer;
